'use client';
import * as React from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '../cn';

export const inputClass =
  'w-full h-9 px-3 rounded-[var(--radius-sm)] bg-fill-3 text-text placeholder:text-text-3 text-[14px] transition-[background-color,box-shadow] duration-[var(--duration)] hover:bg-fill-2 focus:bg-surface focus:shadow-[0_0_0_3px_var(--accent-ring),0_0_0_1px_var(--accent)] disabled:opacity-50 aria-[invalid=true]:shadow-[0_0_0_1px_var(--red)]';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
}
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input({ className, leading, trailing, ...props }, ref) {
  if (!leading && !trailing) return <input ref={ref} className={cn(inputClass, className)} {...props} />;
  return (
    <div className="relative">
      {leading ? <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-3 [&>svg]:size-4 pointer-events-none">{leading}</span> : null}
      <input ref={ref} className={cn(inputClass, leading && 'pl-9', trailing && 'pr-9', className)} {...props} />
      {trailing ? <span className="absolute right-2 top-1/2 -translate-y-1/2 text-text-3 [&>svg]:size-4">{trailing}</span> : null}
    </div>
  );
});

export const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, rows = 3, ...props }, ref) {
  return <textarea ref={ref} rows={rows} className={cn(inputClass, 'h-auto py-2 resize-y leading-[1.45]', className)} {...props} />;
});

export interface SearchFieldProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
  value: string;
  onChange: (value: string) => void;
}
export const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField({ value, onChange, className, placeholder = 'Search', ...props }, ref) {
  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-4 text-text-3 pointer-events-none" aria-hidden />
      <input ref={ref} type="search" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={cn(inputClass, 'pl-8 pr-8 rounded-full h-8')} {...props} />
      {value ? (
        <button type="button" aria-label="Clear" onClick={() => onChange('')} className="absolute right-2 top-1/2 -translate-y-1/2 size-4 rounded-full bg-fill text-surface flex items-center justify-center hover:bg-text-3">
          <X className="size-3" />
        </button>
      ) : null}
    </div>
  );
});

export interface FieldProps {
  label?: React.ReactNode;
  hint?: React.ReactNode;
  error?: React.ReactNode;
  required?: boolean;
  htmlFor?: string;
  children: React.ReactNode;
  className?: string;
  inline?: boolean;
}
/** Label + control + hint/error with consistent 8px rhythm. */
export function Field({ label, hint, error, required, htmlFor, children, className, inline }: FieldProps) {
  return (
    <div className={cn(inline ? 'flex items-center justify-between gap-4' : 'flex flex-col gap-1.5', className)}>
      {label ? (
        <label htmlFor={htmlFor} className="text-[13px] font-medium text-text-2 select-none">
          {label}
          {required ? <span className="text-red ml-0.5">*</span> : null}
        </label>
      ) : null}
      <div className={cn(inline ? 'shrink-0' : 'w-full')}>{children}</div>
      {error ? <p className="text-[12px] text-red">{error}</p> : hint ? <p className="text-[12px] text-text-3">{hint}</p> : null}
    </div>
  );
}

/** Two-column grid for forms. */
export function FormRow({ children, className, cols = 2 }: { children: React.ReactNode; className?: string; cols?: 1 | 2 | 3 }) {
  return <div className={cn('grid gap-4', cols === 2 ? 'sm:grid-cols-2' : cols === 3 ? 'sm:grid-cols-3' : 'grid-cols-1', className)}>{children}</div>;
}

/** Stack for forms with 16px spacing */
export function FormStack({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn('flex flex-col gap-4', className)}>{children}</div>;
}
